import { useRef } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import type { MeResponse } from '@ucp/web/lib';
import { myWorkAppliesToMe } from '../../lib/myWork/applies';
import { SchoolContextProvider } from '../SchoolContextProvider';
import { SchoolDirectoryProvider } from '../SchoolDirectoryProvider';
import { assignedSchoolIds } from '../assignedSchoolIds';
import { ofstedModuleEnabled } from '../ofsted/ofstedRoles';
import { ActiveModuleProvider, useActiveModule } from './ActiveModuleProvider';
import { AppFooter } from './AppFooter';
import { AppHeader } from './AppHeader';
import { AppLayout } from './AppLayout';
import { AppRoutes } from './AppRoutes';
import { LandingRedirect } from './LandingRedirect';
import { MyWorkCountProvider } from '../my-work/MyWorkCount';
import { ModuleNav } from './ModuleNav';
import { ModuleRail } from './ModuleRail';
import { ModuleSubModuleNav } from './ModuleSubModuleNav';
import {
  entitledChrome,
  isModuleDashboard,
  moduleById,
  moduleRootTab,
  portalOpsForPath,
  subModuleForPath,
  subModulesAsPageTabs,
  visibleModules,
  visibleTasks,
} from './modulesConfig';
import { hasAnyRole } from './navConfig';
import { PrimaryNav } from './PrimaryNav';
import { RouteAnnouncer } from './RouteAnnouncer';
import { SubModuleTaskNav } from './SubModuleTaskNav';
import { useRouteTitle } from './useRouteTitle';

/**
 * Task-based, role-gated routed shell (PRO-0250 / ADR-014, + Amendment 1
 * PRO-0340, + Amendment 3 nested deep modules). Mounts only on the authenticated
 * + access-configured path (chosen by `AccessGate`, D2), so no-role users never
 * reach the router. School-tenancy is ambient via the header switcher (D3), not a
 * URL segment (ADR-010). Client route guards are defence-in-depth — the server
 * authorises every data call (capability + RLS, ADR-009/010).
 *
 * Navigation shapes (ADR-014 A3):
 * - **Flat** (Comms): tier-1 module rail + tier-2 `PrimaryNav`.
 * - **Nested** (HR/Finance): sub-modules in the rail when active; module dashboard
 *   at `/hr` / `/finance` has no tier-2; sub-module routes show `SubModuleTaskNav`.
 * - **Nested + page-tab sub-modules** (functional Complaints; grouped nested members
 *   Complaints + Referrals, PRO-0600 follow-up): the sub-module strip renders on every
 *   breakpoint (`ModuleSubModuleNav` all-viewport, `subModuleNav` slot) and the rail
 *   carries no nested list. If the sub-modules own tasks (Referrals), the active
 *   sub-module's `SubModuleTaskNav` still renders below — the ATR task-bar pattern.
 */
function ShellContent({ user, onSignOut }: { user: MeResponse; onSignOut: () => void }) {
  const mainRef = useRef<HTMLElement | null>(null);
  const complaintsEnabled = user.featureFlags.complaintsEnabled;
  // PRO-0590 / ADR-038 — Insights is hidden entirely unless the flag mounts it.
  // `visibleModules` filters the tier-1 registry once here; threaded into the rail
  // and mobile module drawer so no nav component reads the flag directly.
  const insightsEnabled = user.featureFlags.insightsEnabled;
  // PRO-0920 / ADR-029 A3 — threaded once here (mirrors the `insightsEnabled` site);
  // hides the `commercial` (CDP) tier-1 module until `UCP_COMMERCIAL_DATA_ENABLED`, so
  // the grouped rail's Commercial group shows Referrals + Commercial only when on.
  const commercialDataEnabled = user.featureFlags.commercialDataEnabled ?? false;
  // PRO-3050 — hides the `attendance` tier-1 module until `UCP_ATTENDANCE_ENABLED`.
  const attendanceEnabled = user.featureFlags.attendanceEnabled === true;
  // PRO-0710 — hides the `trips` tier-1 module until `UCP_TRIPS_ENABLED`.
  const tripsEnabled = user.featureFlags.tripsEnabled === true;
  // PRO-3100 - hides the `weekly-report` tier-1 module until `UCP_OPS_ISSUES_ENABLED`.
  const opsIssuesEnabled = user.featureFlags.opsIssuesEnabled === true;
  // PRO-3140 / PRO-3150 - hides the `ofsted` tier-1 module until readiness or inspection is on.
  const ofstedEnabled = ofstedModuleEnabled(user.featureFlags);
  // PRO-3210 - hides the `marketing` tier-1 module until `UCP_MARKETING_REQUESTS_ENABLED`.
  const marketingEnabled = user.featureFlags.marketingRequestsEnabled === true;
  // PRO-3220 Phase 3 - hides the `pupil-risk` tier-1 module until `UCP_PUPIL_RISK_ENABLED`.
  const pupilRiskEnabled = user.featureFlags.pupilRiskEnabled === true;
  // The single chrome-filter site (PRO-1360 / ADR-014 A14). With
  // `navEntitlementFilterEnabled` on, a module, group member, section link or page tab
  // survives only where the caller would be admitted by the same role + flag test its
  // RouteGuard uses, and coming-soon placeholders leave the chrome entirely. With it
  // off this is the shipped `entitledModules` behaviour byte-for-byte: unentitled
  // FUNCTIONAL sub-module links hidden, placeholders still shown.
  //
  // Filtering here rather than in each renderer is what keeps the invariant provable —
  // every downstream nav component consumes this one array. The server stays
  // authoritative for every route regardless.
  const modules = entitledChrome(
    visibleModules(
      insightsEnabled,
      commercialDataEnabled,
      attendanceEnabled,
      tripsEnabled,
      opsIssuesEnabled,
      ofstedEnabled,
      marketingEnabled,
      pupilRiskEnabled,
    ),
    user,
    user.featureFlags,
  );
  // PRO-0600 / ADR-014 A8 — threaded once here (mirrors the `visibleModules` site);
  // the nav components derive the flat vs grouped rail model from this boolean.
  const navRestructureEnabled = user.featureFlags.navRestructureEnabled;
  // PRO-1360 / ADR-014 A14 — absent reads as OFF (an older API or an unwired
  // environment must degrade to the shipped chrome, never to a wider one).
  const navEntitlementFilterEnabled = user.featureFlags.navEntitlementFilterEnabled === true;
  // PRO-0740 / ADR-014 amendment — threaded once here (mirrors `complaintsEnabled`);
  // drops the re-homed Referrals My Coverage / Service Profiles sub-module tabs.
  const profileSettingsEnabled = user.featureFlags.profileSettingsEnabled;
  // ADR-038 A3 — the role-adaptive Referrals home dashboard at `/referrals`. When on,
  // surface it as the leading tier-2 tab so it is reachable without the tier-1 rail.
  const referralsDashboardEnabled = user.featureFlags.referralsDashboardEnabled;
  const title = useRouteTitle(mainRef, complaintsEnabled);
  const activeModuleId = useActiveModule();
  // Use the entitlement-filtered module (so the sub-module tab strip / mobile picker
  // hide unentitled links too); fall back to the canonical registry if the active
  // module isn't in the visible set (e.g. a flag-hidden flat module).
  const activeModule =
    modules.find((module) => module.id === activeModuleId) ?? moduleById(activeModuleId);
  const { pathname } = useLocation();

  const isL6 = hasAnyRole(user, ['L6_PLATFORM_ADMIN']);
  const activePortalOpsId = portalOpsForPath(pathname);
  // PRO-0740 / ADR-014 amendment (MF-2) — `/account` is NOT a module: suppress both
  // the tier-2 task nav and any tier-1 active-module highlight (moduleForPath falls
  // back to `'comms'` for it), mirroring the `activePortalOpsId` handling.
  const isAccountRoute = pathname === '/account';

  const isNested = activeModule.navShape === 'nested';
  const onDashboard = isModuleDashboard(pathname, complaintsEnabled);
  const activeSubModule =
    isNested && !onDashboard ? subModuleForPath(pathname, complaintsEnabled) : undefined;
  // PRO-0600 follow-up — a nested module that page-tabs its sub-modules (functional
  // Complaints; grouped nested members like Complaints + Referrals) shows the
  // sub-module strip on every breakpoint (not just the mobile picker) and the rail
  // carries no nested list, so the sub-module links appear exactly once. When the
  // sub-modules own tasks (Referrals), the active sub-module's task strip still
  // renders below (the `nav` slot), giving the mobile two-strip layout everywhere.
  const subModulesAsTabs =
    !activePortalOpsId &&
    subModulesAsPageTabs(
      activeModule,
      complaintsEnabled,
      navRestructureEnabled,
      profileSettingsEnabled,
    );

  // PRO-1360 — `PrimaryNav` renders its `nav` landmark unconditionally, so a caller
  // with no entitled Comms item would otherwise get an EMPTY, named `nav` (a WCAG 2.2
  // AA defect and a chrome element with nothing behind it). Comms is in `modules`
  // whenever it is entitled, and always when the kill-switch is off, so this is inert
  // in the shipped path.
  const commsIsChrome = modules.some((module) => module.id === 'comms');
  // Portal-ops surfaces (`/platform/*`) render their own PlatformSubNav — suppress the
  // Comms task nav so no false Comms active state appears (TD-3 / MF-001).
  let taskNav = null;
  if (activePortalOpsId || isAccountRoute) {
    taskNav = null;
  } else if (activeModuleId === 'comms' && commsIsChrome) {
    taskNav = <PrimaryNav user={user} moduleLabel={activeModule.label} />;
  } else if (
    activeSubModule &&
    visibleTasks(activeSubModule, user.featureFlags, user, {
      hrCaseTrack: user.hrCaseTrack === true,
    }).length > 0
  ) {
    taskNav = <SubModuleTaskNav subModule={activeSubModule} flags={user.featureFlags} user={user} />;
  }

  const shell = (
    <AppLayout
      header={
        <AppHeader
          user={user}
          onSignOut={onSignOut}
          moduleNav={
            <ModuleNav
              activeModuleId={activeModuleId}
              showPortalOps={isL6}
              activePortalOpsId={activePortalOpsId}
              modules={modules}
              navRestructureEnabled={navRestructureEnabled}
              suppressActiveModule={isAccountRoute}
              user={user}
            />
          }
        />
      }
      rail={
        <ModuleRail
          activeModuleId={activeModuleId}
          complaintsEnabled={complaintsEnabled}
          showPortalOps={isL6}
          activePortalOpsId={activePortalOpsId}
          modules={modules}
          navRestructureEnabled={navRestructureEnabled}
          suppressActiveModule={isAccountRoute}
          profileSettingsEnabled={profileSettingsEnabled}
        />
      }
      subModuleNav={
        isNested ? (
          <ModuleSubModuleNav
            module={activeModule}
            complaintsEnabled={complaintsEnabled}
            profileSettingsEnabled={profileSettingsEnabled}
            rootTab={moduleRootTab(activeModule, referralsDashboardEnabled)}
            className={subModulesAsTabs ? '' : 'md:hidden'}
          />
        ) : null
      }
      nav={taskNav}
      announcer={<RouteAnnouncer title={title} />}
      mainRef={mainRef}
      mainLabel={title}
      footer={<AppFooter />}
    >
      {/* The sole owner of the post-auth "front door" redirect; fires once per mount at
          `/`, renders null otherwise. `enabled` is whether this component acts at all;
          `insightsEnabled` is passed separately because it is a SEMANTIC input to the
          landing resolver, and conflating the two would send a SCHOOL_STAFF caller to
          an /insights route that is not mounted (PRO-0600 Inc 2 MF-01; PRO-1360). */}
      <LandingRedirect
        user={user}
        enabled={(navRestructureEnabled && insightsEnabled) || navEntitlementFilterEnabled}
        insightsEnabled={insightsEnabled}
        flags={user.featureFlags}
      />
      <AppRoutes user={user} />
    </AppLayout>
  );
  return myWorkAppliesToMe(user) ? <MyWorkCountProvider>{shell}</MyWorkCountProvider> : shell;
}

export function RoutedAppShell({ user, onSignOut }: { user: MeResponse; onSignOut: () => void }) {
  return (
    <SchoolContextProvider availableSchoolIds={assignedSchoolIds(user)}>
      <SchoolDirectoryProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ActiveModuleProvider>
            <ShellContent user={user} onSignOut={onSignOut} />
          </ActiveModuleProvider>
        </BrowserRouter>
      </SchoolDirectoryProvider>
    </SchoolContextProvider>
  );
}
